/**
 * What the tax returns page keeps in its address: the company, the tab, the
 * page and every filter and sort of the tab on screen, so a link or a reload
 * opens the same list. Tabs share `offset`/`limit` (the app's table reads
 * them straight off the address); a tab's own filters are dropped when
 * another tab is opened.
 */
export const TAX_RETURNS_DEFAULT_PARAMS = Object.freeze({
  offset: '1',
  limit: '20',
  tab: 'returns',
});

const SORTS = {
  returns: ['sortProject', 'sortTitle', 'sortPurpose', 'sortFiledAt', 'sortDueDate', 'sortAmount', 'sortPaidAmount'],
  calculations: ['sortPeriod', 'sortProject', 'sortSubject', 'sortNetRsd', 'sortTax'],
  people: [
    'sortAddress',
    'sortProject',
    'sortNetPerMonth',
    'sortContractTo',
    'sortLastCalculation',
    'sortName',
    'sortIdNumber',
    'sortAccount',
  ],
  accounts: ['sortAccount', 'sortBank', 'sortLatest', 'sortStatements', 'sortGaps'],
};

/** The filters of each tab, as they go to the server. */
export const TAB_FILTERS = Object.freeze({
  // `month` may hold several months (`9,10`); `monthBy` is `due`, or absent for the filing date.
  returns: ['search', 'project', 'year', 'month', 'monthBy', 'status', 'category', 'type', 'dueSoon', 'interest'],
  calculations: ['search', 'project', 'year', 'month', 'kind', 'status'],
  people: ['search', 'project', 'inactive'],
  accounts: ['search', 'project'],
});

/** Every filter and sort key of a tab set to undefined: what „clear all filters" writes. */
export const clearedParams = (tab) =>
  Object.fromEntries([...(TAB_FILTERS[tab] || []), ...(SORTS[tab] || [])].map((k) => [k, undefined]));

/** Everything any tab may have left in the address, for a change of tab or company. */
export const ALL_CLEARED = Object.freeze(
  Object.assign({}, ...Object.keys(TAB_FILTERS).map(clearedParams), { view: undefined })
);

/** A tab's filters and sorts as they are in the address, for its request. */
export const paramsOf = (tab, queryParams) => {
  const keys = [...TAB_FILTERS[tab], ...SORTS[tab], 'offset', 'limit'];
  // The address reads `9,10` back as a list; it goes to the server as written, `month=9,10`.
  return Object.fromEntries(
    keys
      .filter((k) => queryParams[k] !== undefined && queryParams[k] !== '')
      .map((k) => [k, Array.isArray(queryParams[k]) ? queryParams[k].join(',') : queryParams[k]])
  );
};

/** The filters without the page: what a workbook export is built from. */
export const withoutPaging = (params) =>
  Object.fromEntries(Object.entries(params).filter(([k]) => k !== 'offset' && k !== 'limit'));
